import { createPortal } from "react-dom";
import type { CoachListItem } from "@/types/api";
import { resolveCustomerAssetUrl } from "@/lib/asset";

interface CoachSwitchModalProps {
  open: boolean;
  loading?: boolean;
  coaches: CoachListItem[];
  onClose: () => void;
  onSelect: (coach: CoachListItem) => void;
}

function resolveCoachImage(coach: CoachListItem) {
  return resolveCustomerAssetUrl(coach.workPhoto) || "";
}

function buildCoachMeta(coach: CoachListItem) {
  const parts: string[] = [];
  if (coach.distanceDesc) {
    parts.push(String(coach.distanceDesc));
  }
  const earliest = String(coach.earliestTime || coach.first_appoint || "").trim();
  if (earliest) {
    parts.push(earliest);
  }
  return parts.join(" · ");
}

export function CoachSwitchModal({
  open,
  loading = false,
  coaches,
  onClose,
  onSelect,
}: CoachSwitchModalProps) {
  if (!open || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex items-end justify-center"
      style={{ background: "rgba(17, 24, 39, 0.45)" }}
      onClick={onClose}
    >
      <div
        className="w-full rounded-t-[28px] bg-white"
        style={{ maxWidth: 480, maxHeight: "78vh" }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex justify-center pt-3 pb-1">
          <div className="h-1 w-10 rounded-full bg-[#E7DED6]" />
        </div>

        <div className="px-5 pb-3 pt-3">
          <div className="mb-2 inline-flex items-center gap-1 rounded-full bg-[#FFF4EA] px-3 py-1 text-xs font-semibold text-[#FF6A1A]">
            <i className="ri-rest-time-line text-[13px]" />
            当前技师休息中
          </div>
          <h3 className="text-[18px] font-bold text-[#1A1A1A]">为你推荐可服务技师</h3>
          <p className="mt-1 text-xs leading-5 text-[#9A8F86]">
            选择其他可预约技师后，将直接进入对方详情页继续下单。
          </p>
        </div>

        <div className="max-h-[52vh] overflow-y-auto px-4 pb-4">
          {loading ? (
            <div className="rounded-[22px] border border-[#F3E8DE] bg-[#FFFCF9] px-4 py-8 text-center text-sm text-[#A69486]">
              正在为你匹配可服务技师...
            </div>
          ) : coaches.length > 0 ? (
            <div className="space-y-3">
              {coaches.map((coach) => (
                <button
                  key={coach.id}
                  type="button"
                  onClick={() => onSelect(coach)}
                  className="flex w-full items-center gap-3 rounded-[22px] border border-[#F2E8DE] bg-[#FFFDFB] px-4 py-4 text-left"
                >
                  <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-[18px] bg-[#F5F3EF]">
                    {resolveCoachImage(coach) ? (
                      <img
                        src={resolveCoachImage(coach)}
                        alt={coach.name || "技师"}
                        className="h-full w-full object-cover object-top"
                      />
                    ) : null}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-center gap-2">
                      <span className="truncate text-sm font-bold text-[#1A1A1A]">
                        {coach.name || "可服务技师"}
                      </span>
                      <span className="rounded-full bg-[#EAFBF1] px-2 py-0.5 text-[11px] font-semibold text-[#16A46B]">
                        可预约
                      </span>
                    </div>

                    <div className="mb-1 flex items-center gap-1 text-xs text-[#FF8A34]">
                      <i className="ri-star-fill text-[12px]" />
                      <span>{Number(coach.rating || 0).toFixed(1)}</span>
                    </div>

                    {buildCoachMeta(coach) ? (
                      <p className="line-clamp-1 text-xs text-[#9A8F86]">{buildCoachMeta(coach)}</p>
                    ) : null}
                  </div>

                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#FFF2E8] text-[#FF6A1A]">
                    <i className="ri-arrow-right-up-line text-base" />
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="rounded-[22px] border border-[#F3E8DE] bg-[#FFFCF9] px-4 py-8 text-center">
              <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-[#FFF1E6] text-[#FF6A1A]">
                <i className="ri-user-search-line text-lg" />
              </div>
              <p className="text-sm font-semibold text-[#6F635A]">暂未找到可服务技师</p>
              <p className="mt-1 text-xs text-[#A69486]">可以稍后再试，或者返回首页重新筛选。</p>
            </div>
          )}
        </div>

        <div className="flex gap-3 px-4 pb-5">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-[18px] border border-[#ECDDD0] bg-white py-3 text-sm font-semibold text-[#7A6F66]"
          >
            暂不切换
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-[18px] py-3 text-sm font-semibold text-white"
            style={{ background: "linear-gradient(90deg, #FF8C3A, #FF5C00)" }}
          >
            我知道了
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
